<script setup lang="ts">
import type { AppTextareaEvent } from '@shared/ui/fields'
import type { RubricEditorProps } from '../../../../model/assessments/rubric-editors.hook'

import { AppText } from '@shared/ui/typography'
import { AppTextarea } from '@shared/ui/fields'

import { useRubricLevels } from '../../../../model/assessments/rubric-editors.hook'

defineOptions({
  name: 'RubricLevelList',
})

const props = defineProps<RubricEditorProps>()

const {
  refInstructions,
  refLevels,
  refIsAuthorable,
  refExtraLevelsCount,
  refExtraLevelsNote,
  setInstructions,
  setLevelDescription,
} = useRubricLevels({ rubricId: () => props.rubricId })
</script>

<template>
  <div class="rubric-level-list">
    <!-- No "Levels" heading and no add button: the three bands ARE the form, so a heading over
         a fixed three-row grid would only cost vertical space. The score is a chip, not an
         input — the bands are 0, 1, 2 and cannot be renumbered. -->
    <AppText
      v-if="!refIsAuthorable"
      name="body2"
      color="slate-500"
    >
      This rubric is stored in a shape this editor cannot author, so it has no levels to show.
      Validation reports it on save.
    </AppText>

    <ul
      v-else
      class="rubric-level-list__items"
    >
      <li
        v-for="level in refLevels"
        :key="level.score"
        class="rubric-level-list__item"
      >
        <AppText
          name="body2-medium"
          color="slate-500"
          class="rubric-level-list__score"
        >
          {{ level.score }}
        </AppText>
        <!-- A textarea, not a single-line input: what earns a band is a sentence, and one that
             scrolled sideways in a text box could not be read back while writing the next
             band. Two rows tall — see `__description` below. -->
        <AppTextarea
          name="level-description"
          :value="level.description"
          placeholder="What the tutor should see to award this score (optional)"
          class="rubric-level-list__description"
          @input="(event: AppTextareaEvent) => setLevelDescription(level.score, event.value)"
        />
      </li>
    </ul>

    <!-- A pre-fixed-bands rubric keeps whatever else it holds (decision B9), and this is the only
         place that says so — the rows above cannot show a band outside 0–2. -->
    <AppText
      v-if="refExtraLevelsCount > 0"
      name="body2"
      color="slate-500"
    >
      {{ refExtraLevelsNote }}
    </AppText>

    <AppTextarea
      name="rubric-instructions"
      placeholder="Instructions for the tutor (optional)"
      :value="refInstructions"
      class="rubric-level-list__instructions"
      @input="(event: AppTextareaEvent) => setInstructions(event.value)"
    />
  </div>
</template>

<style scoped lang="scss" src="./RubricLevelList.scss" />
